import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import QRCode from 'qrcode';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import './Cards.css';

import { API_BASE } from '../config';
import { scopeStations } from '../components/roleAccess';
import { tr } from '../i18n';

// Auth token for every /ak4tek/cards/* call — the SAME token issued by
// /auth/login that the rest of the app uses. There is no separate card-manager
// login any more: the backend accepts a normal JWT and enforces roles and
// station scope on top of it.
const authToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('token')).data.access_token;
  } catch (e) {
    return '';
  }
};

// How the station-serial field is entered. The colleague's backend now stores
// station_serial as an ARRAY so one card can authorize at several stations.
// 'chips' = pick/add stations as removable badges (default).
// 'csv'   = one plain text box, serials separated by commas (fallback if chips
//           prove cumbersome). Flip this single constant to switch — both modes
//           read/write the same string[] on formData.station_serial.
const SERIAL_INPUT_MODE = 'chips'; // 'chips' | 'csv'

// station_serial can arrive as an array (current backend), a single string
// (legacy rows), or a comma-joined string — normalize all of them to a clean
// string[] so the rest of the component only ever deals with one shape.
const toSerialArray = (v) =>
  Array.isArray(v)
    ? v.map((s) => String(s).trim()).filter(Boolean)
    : typeof v === 'string' && v.trim()
    ? v.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

// Plug-and-play NFC readers that present as a keyboard ("keyboard wedge") emit
// the card UID as a base-10 integer, then an Enter. The same UID shown in hex on
// an Android reader is just the base-16 form of that integer. Convert with BigInt
// (NOT parseInt/Number): a 7-byte UID is up to 20 decimal digits, which overflows
// Number.MAX_SAFE_INTEGER (~16 digits) and silently corrupts. Pad to an even
// number of hex chars so a leading zero byte (e.g. "0A1B…") isn't dropped.
//
// ★ Byte order: these wedge readers encode the UID LITTLE-ENDIAN (least-
// significant byte first), but the Android reader / enrolled cards / the main
// Frontend reader all use the normal BIG-ENDIAN display order (the UID as
// printed). So after converting, reverse the byte pairs. Verified on a real
// card: dec → 6CAB1B96 → byte-reversed → 961BAB6C (the value the system expects).
const decimalUidToHex = (dec) => {
  try {
    // eslint-disable-next-line no-undef
    let hex = BigInt(dec).toString(16).toUpperCase();
    // Pad the little-endian form up to the next standard UID width (4/7/10 bytes
    // = 8/14/20 hex) before reversing. BigInt drops high-order zero bytes, which
    // are the LSB-first reader's leading 0x00s — i.e. the big-endian UID's
    // TRAILING bytes. Restoring them here keeps a UID like 961BAB00 intact.
    const widths = [8, 14, 20];
    const target = widths.find((w) => hex.length <= w) || (hex.length + (hex.length % 2));
    hex = hex.padStart(target, '0');
    return hex.match(/../g).reverse().join(''); // little-endian → big-endian
  } catch {
    return null; // not a clean integer — leave it alone
  }
};

// Gap (ms) above which two keystrokes are treated as separate events rather than
// part of one scan burst. Humans can't sustain sub-50ms inter-key timing; a
// wedge reader fires every char in well under that. This single threshold is the
// manual-vs-scan discriminator.
const SCAN_GAP_MS = 50;

// One-use QR "order" vouchers are FuelCards whose card_id starts with this
// prefix, generated server-side as ORD-YYYYMMDD-XXXXXX. Mirrors
// is_order_card() in cards.py — the API also reports this back as
// `is_order` on every card, but this is needed client-side too so the
// EDIT form can lock preset_value before the user even submits, rather than
// letting them type a change the server will just reject.
const isOrderCardId = (cardId) => typeof cardId === 'string' && cardId.startsWith('ORD-');

const blankCard = {
  card_id: '',
  customer_name: '',
  tin_number: '',
  payment_method: 'cash',
  preset_value: 0,
  preset_type: 'amount', // 'amount' | 'volume' — how the dispenser reads preset_value
  price: '', // integer or null — empty string in the form means null
  credit: 0, // on-account balance; only drawn down for payment_method === 'credit'.
             // Editable on create AND edit now (edit still also has "Add Credit"
             // for a quick top-up without retyping the full balance).
  station_serial: [], // string[] — see toSerialArray / SERIAL_INPUT_MODE
  is_active: true,
  is_customer: 1, // this page is customer-cards only — attendant cards live on their own page
  product: '',    // '' | 'DIESEL' | 'UNLEADED' — '' means either product is accepted at the pump
  // The vehicle's registration plate. Separate from tin_number above, which
  // is the customer's tax identification number — the two are never copied
  // into one another. (Column renamed from car_plate; see cards.py.)
  license_plate: '',
  // pump field intentionally omitted — currently inert on cloud-managed cards
  // (neither server nor Qt enforce it), so we don't expose it in the UI.
  // Server defaults to 0 on POST when omitted. Re-add if/when enforcement lands.
};

// Station-serial editor. Reads/writes a string[] via `serials` + `setSerials`,
// regardless of which SERIAL_INPUT_MODE is active. `stations` powers the
// add-dropdown (and the name shown on each chip).
//
// The free-text "or type a serial" box was removed: every real station comes
// from the dropdown (built from `stations`, the same scoped list the rest of
// the page uses), so free-typing only invited typos that silently created an
// unmatched serial with no station behind it. Picking from the list is now
// the only way in, in both chips and csv... well, csv still free-types by
// design (see below), this only affects chips mode's picker row.
const SerialField = ({ serials, setSerials, stations, stationName }) => {
  const [pickerValue, setPickerValue] = useState('');

  const addSerial = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return;
    setSerials(serials.includes(s) ? serials : [...serials, s]);
  };
  const removeSerial = (s) => setSerials(serials.filter((x) => x !== s));

  if (SERIAL_INPUT_MODE === 'csv') {
    return (
      <Form.Group className="mb-3">
        <Form.Label>{tr("Station Serials *")}</Form.Label>
        <Form.Control
          type="text"
          value={serials.join(', ')}
          onChange={(e) => setSerials(toSerialArray(e.target.value))}
          placeholder={tr("e.g. 10TZ100614, 10TZ100615")}
        />
        <Form.Text className="text-muted">
          {tr("Comma-separated. One card can be valid at several stations.")}
        </Form.Text>
      </Form.Group>
    );
  }

  // chips mode
  const available = stations.filter((s) => !serials.includes(s.serial_number));
  return (
    <Form.Group className="mb-3">
      <Form.Label>{tr("Station Serials *")}</Form.Label>
      {serials.length > 0 && (
        <div className="mb-2 d-flex flex-wrap" style={{ gap: '0.4rem' }}>
          {serials.map((s) => (
            <span
              key={s}
              className="badge bg-primary d-inline-flex align-items-center"
              style={{ fontSize: '0.85em', fontWeight: 500 }}
              title={s}
            >
              {stationName(s)}
              <button
                type="button"
                aria-label={tr("Remove {0}", s)}
                onClick={() => removeSerial(s)}
                style={{
                  marginLeft: '0.5em',
                  border: 'none',
                  background: 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.1em',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <Form.Control
        as="select"
        value={pickerValue}
        onChange={(e) => {
          addSerial(e.target.value);
          setPickerValue('');
        }}
      >
        <option value="">
          {available.length ? tr("Add a station…") : tr("All stations added")}
        </option>
        {available.map((s) => (
          <option key={s.serial_number} value={s.serial_number}>
            {s.name} ({s.serial_number})
          </option>
        ))}
      </Form.Control>
      <Form.Text className="text-muted">
        {tr("One card can be valid at several stations. Pick from the list to add.")}
      </Form.Text>
    </Form.Group>
  );
};

// Card form used by both Add and Edit modals. card_id is the immutable key
// (that's how the Qt side looks the card up — renaming would orphan a card
// mid-shift), so it's disabled on edit. station_serial IS editable: the whole
// point of the array is changing which stations a card works at over time.
const CardFormFields = ({ formData, setFormData, stations, isEdit, stationName, cards }) => {
  // QR is just an encoding of card_id itself, so it's a clean drop-in: on Add we
  // mint a random hex ID (matching the main Frontend) and fill card_id; on Edit
  // card_id is locked, so we just re-encode the existing value for reprinting.
  const [qrCodeData, setQrCodeData] = useState('');

  // Known customers, deduped by name (case/space-insensitive), each mapped to
  // the TIN from whichever of their existing cards has one set. Built from
  // `cards` (all cards already loaded on the page) rather than a fresh
  // request — this is a lookup table for the form, not new data.
  const knownCustomers = React.useMemo(() => {
    const byKey = new Map();
    for (const c of cards || []) {
      const name = (c.customer_name || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, { name, tin_number: c.tin_number || '' });
      } else if (!existing.tin_number && c.tin_number) {
        // First card for this customer had no TIN on file; a later one does.
        existing.tin_number = c.tin_number;
      }
    }
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [cards]);

  // Keyboard-wedge scan detection for the Card ID field. We accumulate keystrokes
  // only while they arrive in a tight burst (< SCAN_GAP_MS apart); any human-paced
  // gap resets the buffer, so manual typing never accumulates and is left exactly
  // as typed. A wedge reader fills the buffer then sends Enter → we convert the
  // collected decimal UID to hex. all-digits guard keeps a manually typed/pasted
  // hex ID (which contains A–F) from being misread as a decimal scan.
  const scanRef = useRef({ buffer: '', lastTime: 0 });

  const onCardIdKeyDown = (e) => {
    const s = scanRef.current;
    const now = e.timeStamp;
    if (now - s.lastTime > SCAN_GAP_MS) s.buffer = '';
    s.lastTime = now;

    if (e.key === 'Enter') {
      const raw = s.buffer;
      s.buffer = '';
      // Only act if it looks like a fast all-digit burst (a real scan).
      if (raw.length >= 3 && /^[0-9]+$/.test(raw)) {
        e.preventDefault(); // swallow the reader's terminator
        const hex = decimalUidToHex(raw);
        if (hex) setFormData((prev) => ({ ...prev, card_id: hex }));
      }
      return;
    }
    if (e.key.length === 1) s.buffer += e.key;
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? checked
        : type === 'number' ? (value === '' ? '' : Number(value))
        : value,
    }));
  };

  // Customer Name gets its own handler: on ADD only, if the typed name
  // exactly matches (case/space-insensitive) an existing customer, their TIN
  // is pulled in automatically so it isn't retyped for every new card the
  // same customer gets. Not applied on Edit — silently overwriting a TIN
  // someone deliberately changed on an existing card would be a surprise,
  // not a convenience. Only the TIN is pulled in this way: license_plate is
  // per-vehicle, so it is never copied between a customer's cards.
  const onCustomerNameChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, customer_name: value };
      if (!isEdit) {
        const match = knownCustomers.find(
          (c) => c.name.toLowerCase() === value.trim().toLowerCase()
        );
        if (match && match.tin_number) {
          next.tin_number = match.tin_number;
        }
      }
      return next;
    });
  };

  const setSerials = (arr) =>
    setFormData((prev) => ({ ...prev, station_serial: arr }));

  const renderQR = async (id) => {
    return QRCode.toDataURL(id, {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    });
  };

  // Add mode: generate a fresh random 10-char hex ID, fill card_id, show the QR.
  const generateQRCode = async () => {
    try {
      const hexId = Array.from({ length: 10 }, () =>
        Math.floor(Math.random() * 16).toString(16)
      ).join('').toUpperCase();
      const qrDataUrl = await renderQR(hexId);
      setQrCodeData(qrDataUrl);
      setFormData((prev) => ({ ...prev, card_id: hexId }));
    } catch (e) {
      Swal.fire({ title: tr("Error"), text: tr("Failed to generate QR code."), icon: 'error' });
    }
  };

  // Edit mode: card_id is fixed; just encode the existing value so it can be reprinted.
  const showExistingQR = async () => {
    if (!formData.card_id) {
      Swal.fire({ title: tr("Error"), text: tr("This card has no ID to encode."), icon: 'error' });
      return;
    }
    try {
      setQrCodeData(await renderQR(formData.card_id));
    } catch (e) {
      Swal.fire({ title: tr("Error"), text: tr("Failed to generate QR code."), icon: 'error' });
    }
  };

  const printQRCode = () => {
    if (!qrCodeData) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            @page { margin: 0; size: auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { display: flex; justify-content: center; align-items: center; min-height: 100vh; background: white; }
            .qr-container {
              display: flex; justify-content: center; align-items: center; padding: 30px;
              background: white; border: 8px solid #2c3e50; border-radius: 20px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.2); position: relative;
            }
            .qr-container::before {
              content: ''; position: absolute; top: 15px; left: 15px; right: 15px; bottom: 15px;
              border: 2px solid #3498db; border-radius: 12px; pointer-events: none;
            }
            .qr-image { display: block; width: 350px; height: 350px; position: relative; z-index: 1; }
            @media print {
              @page { margin: 0; }
              body { margin: 0; padding: 20mm; }
              .qr-container { box-shadow: none; page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img src="${qrCodeData}" alt="QR Code" class="qr-image" />
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 250);
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Customer Name (company or person)")}</Form.Label>
            <Form.Control
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={onCustomerNameChange}
              list="known-customer-names"
              autoComplete="off"
              placeholder={tr("Start typing to match an existing customer…")}
            />
            {/* Native datalist: browser-driven suggestions from existing
                customers, no extra dependency needed for this project. */}
            <datalist id="known-customer-names">
              {knownCustomers.map((c) => (
                <option key={c.name} value={c.name} />
              ))}
            </datalist>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("TIN Number")}</Form.Label>
            <Form.Control
              type="text"
              name="tin_number"
              value={formData.tin_number}
              onChange={onChange}
            />
            {!isEdit && (
              <Form.Text className="text-muted">
                {tr("Filled in automatically for an existing customer — edit if it's wrong.")}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Card ID *")}</Form.Label>
            <Form.Control
              type="text"
              name="card_id"
              value={formData.card_id}
              onChange={onChange}
              onKeyDown={onCardIdKeyDown}
              disabled={isEdit}
              required
              placeholder={tr("e.g. A1B2C3D4 — or tap a card on the reader")}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* QR code: on Add, generate a fresh ID; on Edit, re-encode the locked card_id. */}
      <div className="qr-panel">
        <h6><b>{tr("QR Code")}</b></h6>
        <p className="qr-panel-desc">
          {isEdit
            ? tr("Show or reprint the QR code for this card ID.")
            : tr("Generate a unique card ID and its scannable QR code.")}
        </p>
        <div style={{ display: 'flex', gap: '10px', marginBottom: qrCodeData ? '15px' : '0', flexWrap: 'wrap' }}>
          {isEdit ? (
            <Button variant="info" size="sm" onClick={showExistingQR}>
              {tr("Show QR")}
            </Button>
          ) : (
            <Button variant="info" size="sm" onClick={generateQRCode}>
              {tr("Generate New QR")}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={printQRCode} disabled={!qrCodeData}>
            {tr("Print QR")}
          </Button>
        </div>
        {qrCodeData && (
          <div className="qr-image-wrap">
            <img
              src={qrCodeData}
              alt={tr("Generated QR Code")}
              style={{ maxWidth: '150px', display: 'block', margin: '0 auto' }}
            />
          </div>
        )}
      </div>
      <SerialField
        serials={toSerialArray(formData.station_serial)}
        setSerials={setSerials}
        stations={stations}
        stationName={stationName}
      />
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Preset Value")}</Form.Label>
            <Form.Control
              type="number"
              name="preset_value"
              value={formData.preset_value}
              onChange={onChange}
              min="0"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Preset Type")}</Form.Label>
            <Form.Control
              as="select"
              name="preset_type"
              value={formData.preset_type}
              onChange={onChange}
            >
              <option value="amount">{tr("amount")}</option>
              <option value="volume">{tr("volume")}</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Payment Method")}</Form.Label>
            <Form.Control
              as="select"
              name="payment_method"
              value={formData.payment_method}
              onChange={onChange}
            >
              <option value="cash">{tr("cash")}</option>
              <option value="credit">{tr("credit")}</option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Product")}</Form.Label>
            <Form.Control
              as="select"
              name="product"
              value={formData.product || ''}
              onChange={onChange}
            >
              <option value="">{tr("Either (Diesel or Unleaded)")}</option>
              <option value="DIESEL">{tr("Diesel only")}</option>
              <option value="UNLEADED">{tr("Unleaded only")}</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("License Plate")}</Form.Label>
            <Form.Control
              type="text"
              name="license_plate"
              value={formData.license_plate || ''}
              onChange={onChange}
              placeholder={tr("e.g. T 324 DLS")}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Price")}</Form.Label>
            <Form.Control
              type="number"
              name="price"
              value={formData.price === null ? '' : formData.price}
              onChange={onChange}
              min="0"
              step="1"
              placeholder={tr("Leave blank for none")}
            />
            <Form.Text className="text-muted">
              {tr("Optional. Leave blank for no price.")}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{tr("Credit")}</Form.Label>
            <Form.Control
              type="number"
              name="credit"
              value={formData.credit}
              onChange={onChange}
              min="0"
            />
            <Form.Text className="text-muted">
              {isEdit
                ? tr("Editing this sets the balance directly. Use “Add Credit” on the card instead if you just want to top up by an amount.")
                : tr("Starting balance. Only drawn down for credit-payment cards.")}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          name="is_active"
          label={tr("Active")}
          checked={formData.is_active}
          onChange={onChange}
        />
      </Form.Group>
    </>
  );
};

const CardModal = ({ show, onHide, isEdit, initialCard, stations, getToken, onSaved, stationName, cards }) => {
  const [formData, setFormData] = useState(blankCard);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      if (isEdit && initialCard) {
        // Normalize the incoming serial shape to a string[] up front so the
        // form only ever deals with an array.
        setFormData({ preset_type: 'amount', ...initialCard, station_serial: toSerialArray(initialCard.station_serial) });
      } else {
        // Add mode: when the account is scoped to exactly one station (e.g. a
        // station Manager), there's nothing to actually choose, so start the
        // form with that station already selected instead of making them
        // open the picker every time.
        const defaultSerial = stations.length === 1 ? [stations[0].serial_number] : [];
        setFormData({ ...blankCard, station_serial: defaultSerial });
      }
    }
  }, [show, isEdit, initialCard, stations]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const serials = toSerialArray(formData.station_serial);
    if (serials.length === 0) {
      Swal.fire({
        title: tr("Station required"),
        text: tr("Add at least one station serial for this card."),
        icon: 'warning',
      });
      return;
    }

    setSaving(true);

    // price: '' (blank) → null; otherwise coerce to integer.
    const price = formData.price === '' || formData.price === null || formData.price === undefined
      ? null
      : parseInt(formData.price, 10);

    const url = isEdit
      ? `${API_BASE}/ak4tek/cards/${encodeURIComponent(formData.card_id)}`
      : `${API_BASE}/ak4tek/cards/`;
    const method = isEdit ? 'PUT' : 'POST';

    // PUT accepts partial bodies. card_id stays immutable (omitted on edit),
    // but station_serial and credit ARE editable now — both sent on edit too.
    const body = isEdit
      ? {
          customer_name:  formData.customer_name,
          tin_number:     formData.tin_number,
          payment_method: formData.payment_method,
          preset_value:   Number(formData.preset_value) || 0,
          preset_type:    formData.preset_type || 'amount',
          product:        formData.product || '',
          license_plate:  formData.license_plate || '',
          price,
          station_serial: serials,
          credit:         Number(formData.credit) || 0,
          is_active:      !!formData.is_active,
        }
      : {
          ...formData,
          station_serial: serials,
          preset_value: Number(formData.preset_value) || 0,
          credit:       Number(formData.credit) || 0,
          price,
          is_active:    !!formData.is_active,
        };

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: getToken(),
        },
        body: JSON.stringify(body),
      });
      const json = await response.json().catch(() => ({}));

      if (response.ok) {
        Swal.fire({
          title: tr("Saved"),
          text: json.message || (isEdit ? tr("Card updated.") : tr("Card created.")),
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
        onSaved();
        onHide();
      } else {
        Swal.fire({
          title: tr("Error {0}", response.status),
          text: json.message || tr("Save failed."),
          icon: 'error',
        });
      }
    } catch (err) {
      Swal.fire({
        title: tr("Connection error"),
        text: String(err),
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      aria-labelledby="card-modal-title"
    >
      <Modal.Header>
        <Modal.Title id="card-modal-title">
          <i
            className={isEdit ? 'fas fa-edit' : 'fas fa-plus-circle'}
            style={{
              marginRight: '0.6em',
              fontSize: '1.25em',
              color: isEdit ? '#ffc107' : '#28a745',
            }}
          ></i>
          {isEdit ? tr("Edit Card") : tr("Add New Card")}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <CardFormFields
            formData={formData}
            setFormData={setFormData}
            stations={stations}
            isEdit={isEdit}
            stationName={stationName}
            cards={cards}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? tr("Saving…") : isEdit ? tr("Save Changes") : tr("Create Card")}
          </Button>
          <Button onClick={onHide} disabled={saving}>
            {tr("Close")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AddCreditModal = ({ show, onHide, card, getToken, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (show) setAmount(''); }, [show]);

  if (!card) return null;

  const current = Number(card.credit || 0);

  const submit = async (e) => {
    e.preventDefault();
    const delta = Number(amount);
    if (!delta || delta <= 0) {
      Swal.fire({ title: tr("Enter an amount"), text: tr("Top-up must be a positive number."), icon: 'warning' });
      return;
    }
    setSaving(true);
    try {
      // Fresh read of the live balance before computing the new value.
      const getRes = await fetch(
        `${API_BASE}/ak4tek/cards/${encodeURIComponent(card.card_id)}`,
        { headers: { Authorization: getToken() } }
      );
      const getJson = await getRes.json().catch(() => ({}));
      if (!getRes.ok) {
        Swal.fire({ title: tr("Error {0}", getRes.status), text: getJson.message || tr("Could not read current balance."), icon: 'error' });
        setSaving(false);
        return;
      }
      const live = Number((getJson.data && getJson.data.credit) ?? card.credit ?? 0);
      const next = live + delta;

      const putRes = await fetch(
        `${API_BASE}/ak4tek/cards/${encodeURIComponent(card.card_id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify({ credit: next }),
        }
      );
      const putJson = await putRes.json().catch(() => ({}));
      if (putRes.ok) {
        Swal.fire({ title: tr("Credit added"), text: tr("New balance: {0}", next.toLocaleString()), icon: 'success', timer: 1600, showConfirmButton: false });
        onSaved();
        onHide();
      } else {
        Swal.fire({ title: tr("Error {0}", putRes.status), text: putJson.message || tr("Top-up failed."), icon: 'error' });
      }
    } catch (err) {
      Swal.fire({ title: tr("Connection error"), text: String(err), icon: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={submit}>
        <Modal.Header>
          <Modal.Title>{tr("Add Credit — {0}", card.card_id)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            {tr("Customer:")} <b>{card.customer_name || '—'}</b>
            <br />
            {tr("Current balance:")}{' '}
            <b style={{ color: current < 0 ? '#c0392b' : 'inherit' }}>
              {current.toLocaleString()}
            </b>
          </p>
          <Form.Group>
            <Form.Label>{tr("Amount to add")}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="any"
              value={amount}
              autoFocus
              onChange={(e) => setAmount(e.target.value)}
              placeholder={tr("e.g. 50000")}
            />
            {Number(amount) > 0 && (
              <Form.Text className="text-muted">
                {tr("New balance will be {0}.", (current + Number(amount)).toLocaleString())}
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            {tr("Cancel")}
          </Button>
          <Button variant="success" type="submit" disabled={saving}>
            {saving ? tr("Adding…") : tr("Add Credit")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const Cards = ({ token, LogoutTimeout }) => {
  const [cards, setCards] = useState([]);
  const [stations, setStations] = useState([]);
  const [stationFilter, setStationFilter] = useState(''); // '' = all
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  // NOTE: token is intentionally NOT captured once here (it used to be:
  // `const cardsToken = authToken();`). That value gets frozen at whatever
  // moment this component last rendered, and doesn't update just because
  // sessionStorage's token is later refreshed/rotated in the background -
  // any request made later using that frozen value can 401 with "Invalid
  // or expired token" even though a valid token exists right now. Every
  // consumer below calls authToken() itself, at the moment it actually
  // makes a request, so it's always using whatever is currently valid.
  const [addShow, setAddShow] = useState(false);
  const [editShow, setEditShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addCreditShow, setAddCreditShow] = useState(false);
  const [addCreditCard, setAddCreditCard] = useState(null);
  const [sortBy, setSortBy] = useState(null);     // column key, null = API order
  const [sortDir, setSortDir] = useState('asc');  // 'asc' | 'desc'

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };


  // Stations list — same /station/all that Reports.js uses. Scoped by role
  // via scopeStations: Manager sees only their own station, Cluster Manager
  // sees only their assigned stations, global roles see everything.
  //
  // When the scoped list comes back with exactly one station (a Manager
  // account tied to a single station), the "Filter by station" dropdown is
  // also defaulted to it — nothing else to choose, so there's no reason to
  // make them pick it themselves.
  const fetchStations = useCallback(async () => {
    try {
      const tok = JSON.parse(sessionStorage.getItem('token')).data.access_token;
      const r = await fetch(`${API_BASE}/station/all`, {
        headers: { Authorization: tok },
      });
      const j = await r.json();
      if (j && j.data) {
        // VFD+ATG only — ATG-only stations don't have fuel cards. Mirrors the
        // is_vfd filter used in Reports.js so the Cards page sees the same
        // station universe as the rest of the app.
        const vfdStations = j.data
          .filter((s) => s.is_vfd)
          .map((s) => ({ name: s.name, serial_number: s.serial_number }));
        const scoped = scopeStations(token, vfdStations);
        setStations(scoped);
        if (scoped.length === 1) {
          setStationFilter(scoped[0].serial_number);
        }
      }
    } catch (err) {
      // Non-fatal — the table can still render, station filter just stays empty.
      console.warn('Could not fetch stations:', err);
    }
  }, [token]);

  // Cards list — uses /cards/?station_serial=X when a filter is set, /cards/all
  // otherwise. Auto-relogs once on 401 in case the cached token has expired.
  const fetchCards = useCallback(
    async (filterSerial) => {
      setLoading(true);
      const tok = authToken();
      if (!tok) {
        setLoading(false);
        return;
      }

      const url = filterSerial
        ? `${API_BASE}/ak4tek/cards/?station_serial=${encodeURIComponent(filterSerial)}`
        : `${API_BASE}/ak4tek/cards/all`;

      // A 401 here means the session has expired. There is no silent
      // re-login to fall back on any more, which is the point — the user is
      // sent back through the normal login like every other page.
      const response = await fetch(url, { headers: { Authorization: tok } });

      try {
        const j = await response.json();
        if (response.ok && j && Array.isArray(j.data)) {
          // Customer-facing page: only show regular customer cards. Attendant
          // cards (is_customer=0) live on their own page, and one-use order
          // vouchers (card_id starting ORD-) live on the Orders page — both
          // are excluded here rather than shown with mismatched actions.
          setCards(
            j.data.filter(
              (c) => Number(c.is_customer) !== 0 && !(c.is_order || isOrderCardId(c.card_id))
            )
          );
        } else {
          setCards([]);
        }
      } catch {
        setCards([]);
      }
      setLoading(false);
    },
    []
  );

  // First-mount: stations, token if missing, initial card list.
  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  useEffect(() => {
    fetchCards(stationFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter]);

  const handleDelete = async (card) => {
    const result = await Swal.fire({
      title: tr("Delete this card?"),
      html: `<b>${card.card_id}</b><br/>${card.customer_name || ''}<br/><small>${toSerialArray(card.station_serial).join(', ')}</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: tr("Yes, delete"),
      cancelButtonText: tr("Cancel"),
      confirmButtonColor: '#d33',
    });
    if (!result.isConfirmed) return;

    try {
      const r = await fetch(
        `${API_BASE}/ak4tek/cards/${encodeURIComponent(card.card_id)}`,
        { method: 'DELETE', headers: { Authorization: authToken() } }
      );
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        Swal.fire({
          title: tr("Deleted"),
          text: j.message || tr("Card removed."),
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
        fetchCards(stationFilter);
      } else {
        Swal.fire({
          title: tr("Error {0}", r.status),
          text: j.message || tr("Delete failed."),
          icon: 'error',
        });
      }
    } catch (err) {
      Swal.fire({ title: tr("Connection error"), text: String(err), icon: 'error' });
    }
  };

  const openEdit = (card) => {
    setEditing(card);
    setEditShow(true);
  };

  const openAddCredit = (card) => {
    setAddCreditCard(card);
    setAddCreditShow(true);
  };

  // Client-side text filter on top of whatever the server returned.
  const lc = searchText.trim().toLowerCase();
  const visibleCards = lc
    ? cards.filter(
        (c) =>
          (c.card_id || '').toLowerCase().includes(lc) ||
          (c.customer_name || '').toLowerCase().includes(lc) ||
          (c.tin_number || '').toLowerCase().includes(lc) ||
          (c.license_plate || '').toLowerCase().includes(lc) ||
          toSerialArray(c.station_serial).join(' ').toLowerCase().includes(lc)
      )
    : cards;

  const stationName = (serial) => {
    const s = stations.find((x) => x.serial_number === serial);
    return s ? s.name : serial;
  };

  // Display string for a card's (possibly multi-) station serials, by name.
  const stationNames = (card) =>
    toSerialArray(card.station_serial).map(stationName).join(', ');

  // Apply sort on top of the filter. sortBy==null means keep API order.
  const sortedCards = sortBy
    ? [...visibleCards].sort((a, b) => {
        const dir = sortDir === 'asc' ? 1 : -1;
        let av, bv;
        switch (sortBy) {
          case 'card_id':        av = a.card_id || '';                  bv = b.card_id || '';                  break;
          case 'customer_name':  av = a.customer_name || '';            bv = b.customer_name || '';            break;
          case 'product':        av = a.product || '';                  bv = b.product || '';                  break;
          case 'license_plate':  av = a.license_plate || '';            bv = b.license_plate || '';            break;
          case 'tin_number':     av = a.tin_number || '';               bv = b.tin_number || '';               break;
          case 'station':        av = stationNames(a);                  bv = stationNames(b);                  break;
          case 'preset_value':   av = Number(a.preset_value) || 0;      bv = Number(b.preset_value) || 0;      break;
          case 'preset_type':    av = a.preset_type || 'amount';        bv = b.preset_type || 'amount';        break;
          case 'credit':         av = Number(a.credit) || 0;            bv = Number(b.credit) || 0;            break;
          case 'payment_method': av = a.payment_method || '';           bv = b.payment_method || '';           break;
          case 'is_active':      av = a.is_active ? 1 : 0;              bv = b.is_active ? 1 : 0;              break;
          case 'created_at':     av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime(); break;
          default: return 0;
        }
        if (typeof av === 'string') return av.localeCompare(bv) * dir;
        return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
      })
    : visibleCards;

  // Header cell with click-to-sort behavior + a visual indicator (▲/▼ when
  // active, ↕ when idle). Inactive arrows stay faint so they don't compete.
  const sortHeader = (label, key) => {
    const isActive = sortBy === key;
    const arrow = isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
    return (
      <th
        onClick={() => handleSort(key)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        title={tr("Click to sort")}
      >
        {label}
        <span style={{ marginLeft: 6, opacity: isActive ? 1 : 0.3, fontSize: '0.75em' }}>
          {arrow}
        </span>
      </th>
    );
  };

  return (
    <div className="CardsPage">
      <div className="FilterBar">
        <h5 className="FilterBarTitle">{tr("Fuel Cards")}</h5>
        <Row style={{ alignItems: 'flex-end' }}>
          <Col md={4}>
            <Form.Group className="mb-0">
              <Form.Label>{tr("Filter by station")}</Form.Label>
              <Form.Control
                as="select"
                value={stationFilter}
                onChange={(e) => setStationFilter(e.target.value)}
              >
                <option value="">{tr("All stations")}</option>
                {stations.map((s) => (
                  <option key={s.serial_number} value={s.serial_number}>
                    {s.name} ({s.serial_number})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-0">
              <Form.Label>{tr("Search")}</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search" ></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={tr("Card ID, customer, TIN, plate…")}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4} style={{ textAlign: 'right' }}>
            <Button variant="primary" onClick={() => setAddShow(true)}>
              {tr("+ Add Card")}
            </Button>{' '}
            <Button
              variant="outline-secondary"
              onClick={() => fetchCards(stationFilter)}
              disabled={loading}
            >
              {loading ? tr("Loading…") : tr("Refresh")}
            </Button>
          </Col>
        </Row>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="table table-striped table-bordered table-hover">
          <thead className="thead-dark">
            <tr>
              {sortHeader(tr("Card ID"),  'card_id')}
              {sortHeader(tr("Customer"), 'customer_name')}
              {sortHeader(tr("Product"),  'product')}
              {sortHeader(tr("Plate"),    'license_plate')}
              {sortHeader(tr("TIN"),      'tin_number')}
              {sortHeader(tr("Station"),  'station')}
              {sortHeader(tr("Preset"),   'preset_value')}
              {sortHeader(tr("Type"),     'preset_type')}
              {sortHeader(tr("Price"),    'price')}
              {sortHeader(tr("Credit"),   'credit')}
              {sortHeader(tr("Payment"),  'payment_method')}
              {sortHeader(tr("Active"),   'is_active')}
              {sortHeader(tr("Created"),  'created_at')}
              <th>{tr("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedCards.length === 0 && !loading && (
              <tr>
                <td colSpan="14" style={{ padding: '3em 1em' }}>
                  <div style={{ textAlign: 'center', color: '#888' }}>
                    <i
                      className="fas fa-credit-card"
                      style={{ fontSize: '3em', color: '#ccc', marginBottom: '0.5em' }}
                    />
                    <div style={{ marginBottom: '1em', fontSize: '1.05em' }}>
                      {searchText || stationFilter
                        ? tr("No cards match your filter.")
                        : tr("No cards yet — add one to get started.")}
                    </div>
                    {!searchText && !stationFilter && (
                      <Button variant="primary" onClick={() => setAddShow(true)}>
                        <i className="fas fa-plus-circle" style={{ marginRight: '0.5em' }} />
                        {tr("Add your first card")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {sortedCards.map((c) => (
              <tr key={c.id || c.card_id}>
                <td>
                  <span className="card-id">
                    {c.card_id}
                  </span>
                </td>
                <td>{c.customer_name}</td>
                <td>{c.product || <span className="text-muted">{tr("Either")}</span>}</td>
                <td>{c.license_plate || <span className="text-muted">—</span>}</td>
                <td>{c.tin_number}</td>
                <td title={toSerialArray(c.station_serial).join(', ')}>
                  <div className="d-flex flex-wrap" style={{ gap: '0.3rem' }}>
                    {toSerialArray(c.station_serial).map((s) => (
                      <span
                        key={s}
                        className="badge bg-light text-dark border"
                        title={s}
                      >
                        {stationName(s)}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>{Number(c.preset_value).toLocaleString()}</td>
                <td>
                  <span className="badge bg-light text-dark border">
                    {c.preset_type || tr("amount")}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {c.price === null || c.price === undefined ? '—' : Number(c.price).toLocaleString()}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <span style={{ color: Number(c.credit) < 0 ? '#c0392b' : 'inherit',
                                 fontWeight: Number(c.credit) < 0 ? 600 : 400 }}>
                    {Number(c.credit || 0).toLocaleString()}
                  </span>
                </td>
                <td>
                  <span
                    className={
                      'badge ' +
                      (c.payment_method === 'credit' ? 'bg-warning text-dark' : 'bg-primary')
                    }
                  >
                    {c.payment_method}
                  </span>
                </td>
                <td>
                  <span className={'badge ' + (c.is_active ? 'bg-success' : 'bg-secondary')}>
                    {c.is_active ? tr("Active") : tr("Inactive")}
                  </span>
                </td>
                <td>
                  <small title={c.created_at}>
                    {c.created_at ? moment(c.created_at).format('MMM D, YYYY h:mm A') : '—'}
                  </small>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => openEdit(c)}
                    title={tr("Edit card")}
                  >
                    <i className="fas fa-edit"></i>
                  </Button>{' '}
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => openAddCredit(c)}
                    title={tr("Add credit")}
                  >
                    <i className="fas fa-coins"></i>
                  </Button>{' '}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(c)}
                    title={tr("Delete card")}
                  >
                    <i className="fas fa-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CardModal
        show={addShow}
        onHide={() => setAddShow(false)}
        isEdit={false}
        initialCard={null}
        stations={stations}
        getToken={authToken}
        onSaved={() => fetchCards(stationFilter)}
        stationName={stationName}
        cards={cards}
      />
      <CardModal
        show={editShow}
        onHide={() => setEditShow(false)}
        isEdit={true}
        initialCard={editing}
        stations={stations}
        getToken={authToken}
        onSaved={() => fetchCards(stationFilter)}
        stationName={stationName}
        cards={cards}
      />
      <AddCreditModal
        show={addCreditShow}
        onHide={() => setAddCreditShow(false)}
        card={addCreditCard}
        getToken={authToken}
        onSaved={() => fetchCards(stationFilter)}
      />
    </div>
  );
};

export default Cards;